import { Sha256 } from '@aws-crypto/sha256-js';
import { HttpRequest } from '@smithy/protocol-http';
import { SignatureV4 } from '@smithy/signature-v4';
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updatePassword,
} from 'aws-amplify/auth';

/**
 * Builds a Cognito user payload compatible with authenticationReducers (v5 shape).
 */
export const buildLoginPayload = async () => {
  const user = await getCurrentUser();
  const attributes = await fetchUserAttributes();
  const username = user.signInDetails?.loginId || user.username;

  return {
    username,
    attributes: {
      ...attributes,
      sub: attributes.sub || user.userId,
    },
  };
};

/**
 * @returns {Promise<object|null>} User info in v5 Auth.currentUserInfo() shape, or null if signed out.
 */
export const getCurrentUserInfo = async () => {
  try {
    return await buildLoginPayload();
  } catch {
    return null;
  }
};

/**
 * @returns {Promise<object|null>} IAM credentials from the identity pool session.
 */
export const getCurrentCredentials = async () => {
  const { credentials } = await fetchAuthSession();
  return credentials || null;
};

/**
 * Signs an API Gateway request (execute-api) for use with axios.
 * Replaces Amplify v5 Signer.sign + Auth.currentCredentials.
 */
export const signApiGatewayRequest = async (method, endpoint, data = '', headers = {}) => {
  const credentials = await getCurrentCredentials();
  if (!credentials) {
    throw new Error('No IAM credentials available. User may not be signed in.');
  }

  const url = new URL(endpoint);
  const noPayload = (method === 'DELETE' || method === 'GET') && (data === '' || data == null);
  const body = noPayload ? undefined : data;

  // SigV4 must sign path and query separately. Embedding `?foo=bar` in `path` makes Smithy
  // URL-encode the whole string into the canonical URI while API Gateway keeps them split.
  const query = {};
  url.searchParams.forEach((value, key) => {
    query[key] = value;
  });

  const httpRequest = new HttpRequest({
    method,
    hostname: url.hostname,
    path: url.pathname,
    ...(Object.keys(query).length ? { query } : {}),
    headers: {
      host: url.hostname,
      Accept: 'application/json',
      ...(noPayload ? {} : { 'Content-Type': 'application/json' }),
      ...headers,
    },
    body,
  });

  // Legacy Amplify Signer did not send x-amz-content-sha256; API Gateway CORS does not allow it.
  const signer = new SignatureV4({
    credentials,
    region: 'eu-west-2',
    service: 'execute-api',
    sha256: Sha256,
    applyChecksum: false,
  });

  const signed = await signer.sign(httpRequest);
  const signedHeaders = { ...signed.headers };
  delete signedHeaders.host;

  return {
    method,
    url: endpoint,
    data: body,
    headers: signedHeaders,
  };
};

export {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updatePassword,
};

/** @param {{ custom: string }} provider */
export const federatedSignIn = ({ customProvider }) =>
  signInWithRedirect({ provider: { custom: customProvider } });
